import "assets/scss/alerts.scss";
import { alertsAPI } from "components/Apis/alerts";
import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchActiveAlerts } from "redux/briefing/alerts";
import AlertCard from "components/Alerts/AlertCard";
import LoadingAnimation from "components/UI/Loader";
import { formatDjangoDateTime } from "utils/dates";
import { useDebouncedValue } from "utils/logic";

const DEFAULT_ALERT_TYPE = "actual_late_arrival";

const AlertsMainView = () => {
    const { alertsList, loading, error, next } = useSelector((state) => state.alerts.activeAlerts);

    const dispatch = useDispatch();

    const [alertType, setAlertType] = useState(DEFAULT_ALERT_TYPE);
    const [alertTypeOptions, setAlertTypeOptions] = useState([]);
    const [team, setTeam] = useState("");
    const [isResolved, setIsResolved] = useState("false");
    const [page, setPage] = useState(1);
    const loadMoreRef = useRef(null);
    const pagingRef = useRef(false);
    const previousFilterParamsRef = useRef(null);
    const debouncedTeam = useDebouncedValue(team, 400);

    const filterParams = useMemo(() => {
        const params = {};
        if (alertType) params.message = alertType;
        if (debouncedTeam?.trim()) params.team = debouncedTeam.trim();
        if (isResolved !== "all") params.is_resolved = isResolved === "true";
        return params;
    }, [alertType, debouncedTeam, isResolved]);

    useEffect(() => {
        let mounted = true;
        alertsAPI.fetchAlertFilters()
            .then((response) => {
                if (!mounted) return;
                const choices = response?.data?.message_choices;
                if (Array.isArray(choices)) {
                    const normalizedChoices = choices.map((choice) => {
                        if (choice && typeof choice === "object") return choice;
                        return { value: choice, label: String(choice) };
                    });
                    setAlertTypeOptions(normalizedChoices);
                    const hasDefault = normalizedChoices.some((option) => option?.value === DEFAULT_ALERT_TYPE);
                    if (hasDefault) {
                        setAlertType(DEFAULT_ALERT_TYPE);
                    }
                }
            })
            .catch(() => {
                if (!mounted) return;
                setAlertTypeOptions([]);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        const previousParams = previousFilterParamsRef.current;
        const filtersChanged =
            !!previousParams && JSON.stringify(previousParams) !== JSON.stringify(filterParams);

        if (filtersChanged && page !== 1) {
            pagingRef.current = false;
            setPage(1);
            previousFilterParamsRef.current = filterParams;
            return;
        }

        dispatch(fetchActiveAlerts({ params: { ...filterParams, page }, append: page > 1 }));
        previousFilterParamsRef.current = filterParams;
    }, [dispatch, filterParams, page]);

    useEffect(() => {
        if (!loading) {
            pagingRef.current = false;
        }
    }, [loading]);

    useEffect(() => {
        if (!loadMoreRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                if (!next || loading || pagingRef.current) return;
                pagingRef.current = true;
                setPage((prev) => prev + 1);
            },
            { rootMargin: "200px" }
        );
        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [next, loading]);

    const list = useMemo(() => {
        if (Array.isArray(alertsList)) return alertsList;
        if (alertsList && Array.isArray(alertsList.results)) return alertsList.results;
        return [];
    }, [alertsList]);

    const formattedList = useMemo(() => (
        list.map((alert) => ({
            ...alert,
            created_at: alert?.created_at ? formatDjangoDateTime(alert.created_at) : alert?.created_at,
            updated_at: alert?.updated_at ? formatDjangoDateTime(alert.updated_at) : alert?.updated_at,
            assigned_at: alert?.assigned_at ? formatDjangoDateTime(alert.assigned_at) : alert?.assigned_at,
            resolved_at: alert?.resolved_at ? formatDjangoDateTime(alert.resolved_at) : alert?.resolved_at,
            target: alert?.target?.target
                ? {
                    ...alert.target,
                    target: {
                        ...alert.target.target,
                        original_scheduled_arrival_time: alert.target.target.original_scheduled_arrival_time
                            ? formatDjangoDateTime(alert.target.target.original_scheduled_arrival_time)
                            : alert.target.target.original_scheduled_arrival_time,
                        scheduled_arrival_time: alert.target.target.scheduled_arrival_time
                            ? formatDjangoDateTime(alert.target.target.scheduled_arrival_time)
                            : alert.target.target.scheduled_arrival_time,
                        estimated_arrival_time: alert.target.target.estimated_arrival_time
                            ? formatDjangoDateTime(alert.target.target.estimated_arrival_time)
                            : alert.target.target.estimated_arrival_time,
                        arrival_time: alert.target.target.arrival_time
                            ? formatDjangoDateTime(alert.target.target.arrival_time)
                            : alert.target.target.arrival_time,
                        departure_time: alert.target.target.departure_time
                            ? formatDjangoDateTime(alert.target.target.departure_time)
                            : alert.target.target.departure_time,
                    },
                }
                : alert?.target,
        }))
    ), [list]);

    const handleRefresh = () => {
        setPage(1);
        dispatch(fetchActiveAlerts({ params: { ...filterParams, page: 1 }, append: false }));
    };

    return (
        <div className="alerts-page">
            <section className="alerts-shell">
                <div className="trends-table-intro">
                    <div>
                        <p className="c-section__eyebrow">Operations</p>
                        <h3 className="trends-table-heading">Alerts</h3>
                    </div>
                    <p className="alerts-subtitle">
                        Monitor and respond to trip-level exceptions and warnings.
                    </p>
                </div>
                <div className="alerts-filter-bar">
                    <div className="alerts-filter-form">
                        <label className="alerts-filter">
                            <span className="alerts-filter__label">Alert Type</span>
                            <SearchableMultiSelect
                                options={alertTypeOptions}
                                value={alertType}
                                onChange={(value) => setAlertType(value || null)}
                                placeholder="All alert types"
                                closeOnSelect
                                single
                                chipLimit={1}
                            />
                        </label>
                        <label className="alerts-filter">
                            <span className="alerts-filter__label">Resolved</span>
                            <select
                                className="alerts-filter__control"
                                value={isResolved}
                                onChange={(event) => setIsResolved(event.target.value)}
                            >
                                <option value="all">All</option>
                                <option value="true">Resolved</option>
                                <option value="false">Unresolved</option>
                            </select>
                        </label>
                        <label className="alerts-filter">
                            <span className="alerts-filter__label">Customer Segment / Service Team</span>
                            <input
                                className="alerts-filter__control"
                                type="text"
                                value={team}
                                onChange={(event) => setTeam(event.target.value)}
                                placeholder="Search team"
                            />
                        </label>
                    </div>
                </div>

                {error && <p className="alerts-error">{typeof error === "string" ? error : "Failed to load alerts."}</p>}

                <div className="alerts-list">
                    {loading && list.length === 0 && (
                        <div className="alerts-loading-state">
                            <LoadingAnimation />
                        </div>
                    )}
                    {list.length === 0 && !loading && <p className="alerts-empty">No alerts found.</p>}
                    {formattedList.map((alert) => {
                        return (
                            <AlertCard
                                key={alert.id || alert.pk}
                                alert={alert}
                                onRefresh={handleRefresh}
                            />
                        );
                    })}
                    <div className="alerts-scroll-sentinel" ref={loadMoreRef} />
                    {loading && list.length > 0 && (
                        <div className="alerts-loading-more">
                            <LoadingAnimation />
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default AlertsMainView;
